import React from "react";
import { Page } from "../../../components/common";
import { useClient } from "../../../hooks";
import { getFormStatus, handleNonFieldErrors } from "../util";
import { useQuery } from "react-query";
import { Nav, NavItem } from "reactstrap";
import { NotificationManager } from "react-notifications";
import { NavLink, Redirect, Route, Switch } from "react-router-dom";
import { Notes } from "../notes";
import { ApplicationForm, References, Referral } from "./components";
import { ApplicationReferenceForm } from "./components/ApplicationReferenceForm";
import { DBS } from "./components/DBS";
import { URLs } from "./api";
import { ParentalConsentForm } from "../parental_consent_form/components/ParentalConsentForm";
import { ParentalConsent } from "./components/ParentalConsent";

export const VolunteerApplicationView = ({ match }) => {
  const { id } = match.params;
  const url = URLs.list + `/${id}`;
  const client = useClient();
  const { data, refetch } = useQuery([url], () => client.get(url).get("data"));

  if (!data) return null;

  const { form, referral } = data;
  const { status, id: formId, is_young_helper } = form;

  async function onFormSubmit(form) {
    try {
      await client.patch(url, {
        form,
      });
      NotificationManager.success("Application updated");
    } catch (ex) {
      handleNonFieldErrors(ex);
    }
    await refetch();
  }

  function getTitle({ form }) {
    const { full_name, email } = form;

    return `Volunteer Application from ${full_name} (${email})`;
  }

  const title = getTitle(data);

  const formUrl = `${window.location.origin}/volunteer_application_forms/${formId}`;

  return (
    <Page title={title}>
      <p className="tw--mt-4">
        {getFormStatus(status)}
        <br />
        <a href={formUrl} target="_blank" rel="noopener noreferrer">
          {formUrl}
        </a>
      </p>
      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            replace
            className="nav-link"
            to={`${match.url}/application_form`}
          >
            Application
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink replace className="nav-link" to={`${match.url}/referral`}>
            Referral
          </NavLink>
        </NavItem>
        {!is_young_helper && (
          <NavItem>
            <NavLink
              replace
              className="nav-link"
              to={`${match.url}/references`}
            >
              References
            </NavLink>
          </NavItem>
        )}
        <NavItem>
          <NavLink replace className="nav-link" to={`${match.url}/notes`}>
            Notes
          </NavLink>
        </NavItem>
        {is_young_helper && (
          <NavItem>
            <NavLink
              replace
              className="nav-link"
              to={`${match.url}/parental_consent`}
            >
              Parental Consent
            </NavLink>
          </NavItem>
        )}
        {!is_young_helper && (
          <NavItem>
            <NavLink replace className="nav-link" to={`${match.url}/dbs`}>
              DBS/PVG/Access NI
            </NavLink>
          </NavItem>
        )}
        <Route
          path={`${match.url}/reference_form`}
          render={() => (
            <NavItem>
              <NavLink
                to={`${match.url}/reference_form`}
                className="nav-link active"
              >
                Reference form
              </NavLink>
            </NavItem>
          )}
        />
        <Route
          path={`${match.url}/parental_consent_form`}
          render={() => (
            <NavItem>
              <NavLink
                to={`${match.url}/parental_consent_form`}
                className="nav-link active"
              >
                Parental consent form
              </NavLink>
            </NavItem>
          )}
        />
      </Nav>
      <Switch>
        <Route path={`${match.url}/application_form`}>
          <ApplicationForm
            key={form}
            defaultValues={form}
            onSubmit={onFormSubmit}
          />
        </Route>
        <Route path={`${match.url}/referral`}>
          <Referral defaultValues={referral} />
        </Route>
        <Route path={`${match.url}/references`}>
          <References match={match} application={id} />
        </Route>
        <Route path={`${match.url}/parental_consent`}>
          <ParentalConsent
            match={match}
            application={data}
            onChanged={refetch}
          />
        </Route>
        <Route path={`${match.url}/dbs`}>
          <DBS match={match} application={data} onChanged={refetch} />
        </Route>
        <Route
          path={`${match.url}/reference_form`}
          render={({ location }) => (
            <ApplicationReferenceForm defaultValues={location.state || {}} />
          )}
        />
        <Route
          path={`${match.url}/parental_consent_form`}
          render={({ location }) => (
            <fieldset disabled>
              <ParentalConsentForm data={location.state || {}} />
            </fieldset>
          )}
        />
        <Route path={`${match.url}/notes`}>
          <Notes
            application={id}
            url="volunteer_application_notes"
            allowChange
          />
        </Route>
        <Redirect to={`${match.url}/application_form`} />
      </Switch>
    </Page>
  );
};
